<template>
  <div class="notification-detail-view">
    <div class="header">
      <h1>File Upload Notification</h1>
      <div class="breadcrumb">
        <router-link to="/portfolio">Portfolio</router-link>
        <span> > </span>
        <span>Notification Details</span>
      </div>
    </div>

    <div v-if="loading" class="loading">
      <div class="spinner"></div>
      <p>Loading notification details...</p>
    </div>

    <div v-else-if="error" class="error">
      <p>{{ error }}</p>
      <button @click="loadNotification" class="retry-btn">Retry</button>
    </div>

    <div v-else-if="notification" class="content">
      <!-- Submission Details Card -->
      <div class="details-card">
        <h2>Submission Details</h2>
        <div class="detail-grid">
          <div class="detail-row">
            <span class="label">Project:</span>
            <span class="value">{{ notification.project_name }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Draw Number:</span>
            <span class="value">Draw {{ notification.draw_number }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Uploaded By:</span>
            <span class="value">{{ notification.uploader_name }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Upload Time:</span>
            <span class="value">{{ formatDate(notification.created_at) }}</span>
          </div>
          <div class="detail-row">
            <span class="label">File Count:</span>
            <span class="value">{{ notification.file_count }} file(s)</span>
          </div>
        </div>
      </div>

      <!-- Files Card -->
      <div class="files-card">
        <h2>Uploaded Files</h2>
        <div v-if="filesLoading" class="files-loading">
          <div class="spinner-small"></div>
          <p>Loading files...</p>
        </div>
        <div v-else-if="files.length === 0" class="no-files">
          <p>No files found</p>
        </div>
        <div v-else class="files-list">
          <div v-for="(file, index) in files" :key="index" class="file-item">
            <div class="file-icon">📄</div>
            <div class="file-info">
              <div class="file-name">{{ file.filename }}</div>
              <div class="file-status" :class="{ 'file-exists': file.exists, 'file-missing': !file.exists }">
                {{ file.exists ? 'Available' : 'File not found' }}
              </div>
            </div>
            <button 
              v-if="file.exists"
              @click="downloadFile(file)" 
              class="download-btn"
            >
              📥 Download
            </button>
            <span v-else class="missing-badge">Missing</span>
          </div>
        </div>
      </div>

      <!-- Actions Card -->
      <div class="actions-card">
        <h2>Next Steps</h2>
        <p class="instructions">
          Download the files above to review them. Click "Process Manually" to open the manual ingestion flow with this project and draw pre-selected.
        </p>
        <div class="action-buttons">
          <button 
            @click="processManually" 
            class="action-btn primary"
          >
            ⚙️ Process Manually (Draw {{ notification.draw_number }})
          </button>
          <button 
            @click="goToProject" 
            class="action-btn secondary"
          >
            🔗 Go to Project: {{ notification.project_name }}
          </button>
          <button 
            v-if="!notification.read"
            @click="markAsRead" 
            class="action-btn tertiary"
          >
            ✓ Mark as Read
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'

const route = useRoute()
const router = useRouter()

const loading = ref(true)
const filesLoading = ref(false)
const error = ref(null)
const notification = ref(null)
const files = ref([])

const notificationId = route.params.id

const loadNotification = async () => {
  try {
    loading.value = true
    error.value = null
    
    const response = await axios.get(`/api/v1/notifications/${notificationId}`)
    notification.value = response.data
    
    // Load files
    await loadFiles()
  } catch (err) {
    console.error('Error loading notification:', err)
    error.value = err.response?.data?.error || 'Failed to load notification details'
  } finally {
    loading.value = false
  }
}

const loadFiles = async () => {
  try {
    filesLoading.value = true
    const response = await axios.get(`/api/v1/notifications/${notificationId}/download`)
    files.value = response.data.files || []
  } catch (err) {
    console.error('Error loading files:', err)
  } finally {
    filesLoading.value = false
  }
}

const downloadFile = async (file) => {
  try {
    // Use the notification download_file endpoint
    const response = await axios.get(`/api/v1/notifications/${notificationId}/download_file`, {
      params: { file_path: file.path },
      responseType: 'blob'
    })
    
    // Create a download link
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', file.filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  } catch (err) {
    console.error('Error downloading file:', err)
    alert('Failed to download file. Please contact support.')
  }
}

const markAsRead = async () => {
  try {
    await axios.patch(`/api/v1/notifications/${notificationId}/mark_read`)
    notification.value.read = true
  } catch (err) {
    console.error('Error marking as read:', err)
  }
}

const processManually = () => {
  if (notification.value && notification.value.project_id && notification.value.draw_number) {
    // Navigate to project with query params to trigger manual upload modal
    router.push({
      path: `/portfolio/project-summary/${notification.value.project_id}`,
      query: {
        openUpload: 'true',
        drawNumber: notification.value.draw_number
      }
    })
  }
}

const goToProject = () => {
  if (notification.value && notification.value.project_id) {
    router.push(`/portfolio/project-summary/${notification.value.project_id}`)
  }
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
}

onMounted(() => {
  loadNotification()
})
</script>

<style scoped lang="scss">
.notification-detail-view {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
  background: #f9fafb;
  min-height: 100vh;
}

.header {
  margin-bottom: 24px;
  
  h1 {
    font-size: 28px;
    font-weight: 600;
    color: #111827;
    margin: 0 0 8px 0;
  }
  
  .breadcrumb {
    font-size: 14px;
    color: #6b7280;
    
    a {
      color: #3b82f6;
      text-decoration: none;
      
      &:hover {
        text-decoration: underline;
      }
    }
    
    span {
      margin: 0 8px;
    }
  }
}

.loading, .error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #e5e7eb;
    border-top: 4px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }
  
  p {
    color: #6b7280;
    margin: 0;
  }
}

.error {
  p {
    color: #ef4444;
    margin-bottom: 16px;
  }
  
  .retry-btn {
    padding: 8px 16px;
    background: #3b82f6;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    
    &:hover {
      background: #2563eb;
    }
  }
}

.content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.details-card, .files-card, .actions-card {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  h2 {
    font-size: 18px;
    font-weight: 600;
    color: #111827;
    margin: 0 0 20px 0;
    padding-bottom: 12px;
    border-bottom: 2px solid #e5e7eb;
  }
}

.detail-grid {
  display: grid;
  gap: 16px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
  
  .label {
    font-weight: 500;
    color: #6b7280;
    font-size: 14px;
  }
  
  .value {
    font-weight: 600;
    color: #111827;
    font-size: 14px;
  }
}

.files-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px;
  
  .spinner-small {
    width: 24px;
    height: 24px;
    border: 3px solid #e5e7eb;
    border-top: 3px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  p {
    color: #6b7280;
    margin: 0;
  }
}

.no-files {
  text-align: center;
  padding: 40px;
  color: #6b7280;
}

.files-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.file-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
  }
  
  .file-icon {
    font-size: 32px;
    flex-shrink: 0;
  }
  
  .file-info {
    flex: 1;
    min-width: 0;
    
    .file-name {
      font-weight: 500;
      color: #111827;
      font-size: 14px;
      margin-bottom: 4px;
      word-break: break-word;
    }
    
    .file-status {
      font-size: 12px;
      font-weight: 500;
      
      &.file-exists {
        color: #10b981;
      }
      
      &.file-missing {
        color: #ef4444;
      }
    }
  }
  
  .download-btn {
    padding: 8px 16px;
    background: #3b82f6;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    font-size: 13px;
    transition: all 0.2s;
    flex-shrink: 0;
    
    &:hover {
      background: #2563eb;
    }
  }
  
  .missing-badge {
    padding: 4px 12px;
    background: #fee2e2;
    color: #991b1b;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
  }
}

.instructions {
  color: #6b7280;
  font-size: 14px;
  line-height: 1.6;
  margin: 0 0 20px 0;
}

.action-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.action-btn {
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  
  &.primary {
    background: #3b82f6;
    color: white;
    
    &:hover {
      background: #2563eb;
    }
  }
  
  &.secondary {
    background: #e5e7eb;
    color: #374151;
    
    &:hover {
      background: #d1d5db;
    }
  }
  
  &.tertiary {
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #f9fafb;
    }
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
